import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { fetchDynamicMenu, setUserInfo } from '@/store/feature/layoutConfigSlice';
import {
  fetchRoleBasedDashboard,
  fetchMedicalReviewerDashboard,
  fetchCaseProcessorDashboard,
  fetchQualityReviewerDashboard,
  fetchRegulatorySubmitterDashboard,
  fetchFinaliseDashboard,
  fetchTriageDashboard,
  fetchDataEntryDashboard,
  fetchAdminDashboard
} from '@/store/feature/dashboardSlice';

export const useDashboardIntegration = () => {
  // Module-level request guards to avoid duplicate API calls across many hook users
  // These persist for the lifetime of the module in the client bundle
  // and ensure we fire at most once per page load per role/event.
  // Note: intentionally not exporting; only used internally for dedupe.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const globalAny = (typeof window !== 'undefined' ? (window as any) : {}) as any;
  if (typeof window !== 'undefined') {
    if (!globalAny.__dashboardRequestedRoles) globalAny.__dashboardRequestedRoles = new Set<string>();
    if (typeof globalAny.__permissionsRequested !== 'boolean') globalAny.__permissionsRequested = false;
  }
  const dispatch = useDispatch();
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);
  const { currentDashboard, loading: dashboardLoading } = useSelector((state: RootState) => state.dashboard);
  const { layoutConfig, loading: menuLoading, permissionsLoaded } = useSelector((state: RootState) => state.layoutConfig as any);

  // Track last role to avoid re-fetch loops across multiple mounts
  const lastRoleForMenuRef = useRef<string | null>(null);
  const lastRoleForDashboardRef = useRef<string | null>(null);

  // Fetch menu configuration when user logs in
  useEffect(() => {
    if (isAuthenticated && user?.role) {
      // Update user info in navbar
      dispatch(setUserInfo({
        firstName: user.name?.split(' ')[0] || '',
        lastName: user.name?.split(' ')[1] || '',
        email: user.email,
        avatar: user.avatar || ''
      }));

      // Fetch user permissions and generate filtered menu (guarded)
      // Moved to Sidebar.tsx
    }
  }, [isAuthenticated, user?.role, permissionsLoaded, menuLoading, dispatch]);

  // Fetch dashboard data based on user role
  useEffect(() => {
    if (isAuthenticated && user?.role) {
      const roleKey = user.role.toLowerCase();
      const roleSet = (typeof window !== 'undefined' ? (window as any).__dashboardRequestedRoles : undefined) as Set<string> | undefined;
      const alreadyRequested = roleSet?.has(roleKey);
      const shouldFetchDashboard = !currentDashboard && !dashboardLoading && lastRoleForDashboardRef.current !== roleKey && !alreadyRequested;
      if (!shouldFetchDashboard) return;

      lastRoleForDashboardRef.current = roleKey;
      if (roleSet) roleSet.add(roleKey);
      switch (roleKey) {
        case 'admin':
          dispatch(fetchAdminDashboard() as any);
          break;
        case 'medicalreviewer':
        case 'medical reviewer':
          dispatch(fetchMedicalReviewerDashboard() as any);
          break;
        case 'caseprocessor':
        case 'case processor':
          dispatch(fetchCaseProcessorDashboard() as any);
          break;
        case 'qualityreviewer':
        case 'quality reviewer':
          dispatch(fetchQualityReviewerDashboard() as any);
          break;
        case 'regulatorysubmitter':
        case 'regulatory submitter':
          dispatch(fetchRegulatorySubmitterDashboard() as any);
          break;
        case 'finaliser':
          dispatch(fetchFinaliseDashboard() as any);
          break;
        case 'triage':
          dispatch(fetchTriageDashboard() as any);
          break;
        case 'dataentry':
        case 'data entry':
          dispatch(fetchDataEntryDashboard() as any);
          break;
        default:
          // Fallback to role-based dashboard (auto-detects role)
          dispatch(fetchRoleBasedDashboard() as any);
          break;
      }
    }
  }, [isAuthenticated, user?.role, currentDashboard, dashboardLoading, dispatch]);

  // Helper function to get dashboard data by role
  const getDashboardByRole = (role: string) => {
    switch (role.toLowerCase()) {
      case 'admin':
        dispatch(fetchAdminDashboard() as any);
        break;
      case 'medicalreviewer':
      case 'medical reviewer':
        dispatch(fetchMedicalReviewerDashboard() as any);
        break;
      case 'caseprocessor':
      case 'case processor':
        dispatch(fetchCaseProcessorDashboard() as any);
        break;
      case 'qualityreviewer':
      case 'quality reviewer':
        dispatch(fetchQualityReviewerDashboard() as any);
        break;
      case 'regulatorysubmitter':
      case 'regulatory submitter':
        dispatch(fetchRegulatorySubmitterDashboard() as any);
        break;
      case 'finaliser':
        dispatch(fetchFinaliseDashboard() as any);
        break;
      case 'triage':
        dispatch(fetchTriageDashboard() as any);
        break;
      case 'dataentry':
      case 'data entry':
        dispatch(fetchDataEntryDashboard() as any);
        break;
      default:
        dispatch(fetchRoleBasedDashboard() as any);
        break;
    }
  };

  // Helper function to refresh current dashboard
  const refreshDashboard = () => {
    if (user?.role) {
      getDashboardByRole(user.role);
    }
  };

  // Helper function to refresh menu
  const refreshMenu = () => {
    if (user?.role) {
      dispatch(fetchDynamicMenu() as any);
    }
  };

  return {
    // Data
    user,
    currentDashboard,
    menuConfig: layoutConfig,

    // Loading states
    isAuthenticated,
    dashboardLoading,
    menuLoading,

    // Actions
    getDashboardByRole,
    refreshDashboard,
    refreshMenu,

    // Helper functions
    isAdmin: user?.role?.toLowerCase() === 'admin',
    isMedicalReviewer: ['medicalreviewer', 'medical reviewer'].includes(user?.role?.toLowerCase() || ''),
    isCaseProcessor: ['caseprocessor', 'case processor'].includes(user?.role?.toLowerCase() || ''),
    isQualityReviewer: ['qualityreviewer', 'quality reviewer'].includes(user?.role?.toLowerCase() || ''),
    isRegulatorySubmitter: ['regulatorysubmitter', 'regulatory submitter'].includes(user?.role?.toLowerCase() || ''),
    isFinaliser: user?.role?.toLowerCase() === 'finaliser',
    isTriage: user?.role?.toLowerCase() === 'triage',
    isDataEntry: ['dataentry', 'data entry'].includes(user?.role?.toLowerCase() || ''),
  };
};
